import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import { FaCrown } from "react-icons/fa"; // Premium icon
import "./Days.css";

interface DayItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Days: React.FC = () => {
  const [data, setData] = useState<DayItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/days`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching days:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: DayItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: DayItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return null;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Days",
    de: "Tage der Woche",
    ta: "நாட்கள்",
    si: "දවස්",
    hi: "दिन",
    ar: "أيام",
    tr: "Günler",
    ru: "Дни",
    zh: "日子",
    fr: "Jours",
    es: "Días",
  };

  const subtitleMap = {
    en: "Days in German",
    de: "Tage auf Deutsch",
    ta: "ஜெர்மன் மொழியில் நாட்கள்",
    si: "ජර්මන් භාෂාවේ දවස්",
    hi: "जर्मन में दिन",
    ar: "أيام بالألمانية",
    tr: "Almanca'da Günler",
    ru: "Дни на немецком",
    zh: "德语中的日子",
    fr: "Jours en allemand",
    es: "Días en alemán",
  };

  return (
    <div className="days-page-container">
      <div className="days-page-header">
        <h1 className="days-page-title">{titleMap["de"]}</h1>
        <p className="days-page-subtitle">{subtitleMap[language]}</p>
      </div>

      {loading ? (
        <div className="days-page-loading">Loading...</div>
      ) : (
        <div className="days-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            const imageUrl = getImageUrl(item.image);

            return (
              <div
                key={item.id}
                className={`days-page-card ${
                  isPlaying ? "days-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="days-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="days-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="days-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Days;
